/**
 * Bildirim Çanı — Görev Yönetimi Faz 3
 *
 * Üst navigasyonda: rozetle okunmamış sayısı, dropdown ile son 20 bildirim,
 * her satırda tür rozet + zaman damgası, click → ilgili görev sayfasına git.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Check, Trash2, X, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const KIND_META = {
  task_assigned: { label: "Yeni Görev", color: "bg-blue-500/20 text-blue-300 border-blue-800/60" },
  task_reassigned: { label: "Yeniden Atandı", color: "bg-blue-500/20 text-blue-300 border-blue-800/60" },
  task_delegated: { label: "Devir İsteği", color: "bg-amber-500/20 text-amber-300 border-amber-800/60" },
  task_delegation_accepted: { label: "Devir ✓", color: "bg-emerald-500/20 text-emerald-300 border-emerald-800/60" },
  task_delegation_rejected: { label: "Devir ✗", color: "bg-rose-500/20 text-rose-300 border-rose-800/60" },
  task_comment_added: { label: "Yorum", color: "bg-sky-500/20 text-sky-300 border-sky-800/60" },
  task_mentioned: { label: "@Etiket", color: "bg-purple-500/20 text-purple-300 border-purple-800/60" },
  task_attachment_added: { label: "Dosya", color: "bg-cyan-500/20 text-cyan-300 border-cyan-800/60" },
  task_reminder_3d: { label: "3 gün kaldı", color: "bg-yellow-500/20 text-yellow-300 border-yellow-800/60" },
  task_reminder_1d: { label: "1 gün kaldı", color: "bg-yellow-500/20 text-yellow-300 border-yellow-800/60" },
  task_reminder_1h: { label: "1 saat kaldı", color: "bg-blue-500/20 text-blue-300 border-blue-800/60" },
  task_overdue: { label: "Gecikti", color: "bg-red-500/20 text-red-300 border-red-800/60" },
  task_completed: { label: "Tamamlandı", color: "bg-emerald-500/20 text-emerald-300 border-emerald-800/60" },
  task_status_changed: { label: "Durum", color: "bg-slate-500/20 text-slate-300 border-slate-700" },
  subtask_completed: { label: "Alt Görev ✓", color: "bg-emerald-500/20 text-emerald-300 border-emerald-800/60" },
};

const relTime = (iso) => {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return "az önce";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} dk önce`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} sa önce`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d} gün önce`;
  return new Date(iso).toLocaleDateString("tr-TR");
};

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const ref = useRef(null);

  const loadUnread = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/unread-count");
      setUnread(data?.unread || 0);
    } catch { /* silent */ }
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications?limit=30");
      setItems(data?.items || []);
      setUnread(data?.unread || 0);
    } catch (e) {
      // Ağ/oturum hatasında sessizce loglayıp boş listeyle devam et — kullanıcıya
      // popup çıkarma. NotificationBell arka planda çalışıyor ve UX'i bozmamalı.
      console.warn("Notifications load failed:", e?.message || e);
      setItems([]);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    loadUnread();
    const iv = setInterval(loadUnread, 60000); // 60s polling
    return () => clearInterval(iv);
  }, [loadUnread]);

  useEffect(() => {
    if (!open) return;
    loadList();
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open, loadList]);

  const onClickNotif = async (n) => {
    // Mark read
    try { await api.post(`/notifications/${n.id}/read`); } catch { /* silent */ }
    setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, is_read: true } : x)));
    setUnread((u) => Math.max(0, u - (n.is_read ? 0 : 1)));
    setOpen(false);
    if (n.link_url) navigate(n.link_url);
    else if (n.task_id) navigate(`/gorevler?task_id=${n.task_id}`);
  };

  const markAllRead = async () => {
    try {
      await api.post("/notifications/read-all");
      setItems((prev) => prev.map((x) => ({ ...x, is_read: true })));
      setUnread(0);
      toast.success("Tüm bildirimler okundu");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteNotif = async (id, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/notifications/${id}`);
      setItems((prev) => prev.filter((x) => x.id !== id));
      loadUnread();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const clearAll = async () => {
    if (!window.confirm("Tüm bildirimler silinecek. Devam edilsin mi?")) return;
    try {
      await api.delete("/notifications");
      setItems([]);
      setUnread(0);
      toast.success("Bildirimler silindi");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="notification-bell"
        onClick={() => setOpen((v) => !v)}
        className="relative p-2 rounded-sm hover:bg-slate-800/60 text-slate-300 hover:text-blue-400 transition-colors"
        aria-label="Bildirimler"
        title="Bildirimler"
      >
        <Bell className="w-5 h-5" strokeWidth={2} />
        {unread > 0 && (
          <span
            data-testid="notification-unread-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-blue-500 text-[10px] font-bold text-white flex items-center justify-center border border-slate-950"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notification-dropdown"
          className="absolute right-0 mt-2 w-96 max-h-[600px] overflow-hidden bg-slate-950 border border-slate-800 rounded-sm shadow-2xl z-50 flex flex-col"
        >
          <div className="px-4 py-3 border-b border-slate-800 flex items-center justify-between">
            <div>
              <div className="text-sm font-bold text-slate-100">Bildirimler</div>
              <div className="text-[10px] text-slate-500 font-mono uppercase tracking-widest">
                {unread} okunmamış · {items.length} toplam
              </div>
            </div>
            <div className="flex items-center gap-1">
              {unread > 0 && (
                <button
                  data-testid="notif-mark-all-read"
                  onClick={markAllRead}
                  className="text-[10px] text-blue-400 hover:text-blue-300 font-mono uppercase px-2 py-1 rounded-sm"
                  title="Tümünü okundu işaretle"
                >
                  <Check className="w-3.5 h-3.5 inline mr-1" />Tümü
                </button>
              )}
              <button onClick={() => setOpen(false)} className="text-slate-500 hover:text-slate-300 p-1" aria-label="Kapat">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="p-8 text-center text-slate-500 text-sm">
                <Loader2 className="w-5 h-5 mx-auto mb-2 animate-spin" /> Yükleniyor…
              </div>
            ) : items.length === 0 ? (
              <div className="p-8 text-center text-slate-500 text-sm">
                <Bell className="w-8 h-8 mx-auto mb-3 opacity-40" />
                Henüz bildirim yok
              </div>
            ) : (
              items.map((n) => {
                const meta = KIND_META[n.kind] || { label: n.kind, color: "bg-slate-800/40 text-slate-400" };
                return (
                  <button
                    key={n.id}
                    data-testid={`notif-${n.id}`}
                    onClick={() => onClickNotif(n)}
                    className={`w-full text-left px-4 py-3 border-b border-slate-900 hover:bg-slate-900/60 transition-colors flex gap-3 ${
                      !n.is_read ? "bg-slate-900/40" : ""
                    }`}
                  >
                    {!n.is_read && <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-2 shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm border ${meta.color}`}>
                          {meta.label}
                        </span>
                        <span className="text-[10px] text-slate-500 ml-auto shrink-0">{relTime(n.created_at)}</span>
                      </div>
                      <div className={`text-xs ${n.is_read ? "text-slate-400" : "text-slate-100 font-semibold"} truncate`}>
                        {n.title}
                      </div>
                      {n.message && (
                        <div className="text-[11px] text-slate-500 mt-0.5 line-clamp-2">
                          {n.message}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={(e) => deleteNotif(n.id, e)}
                      className="text-slate-600 hover:text-rose-400 p-1 shrink-0 self-start"
                      title="Sil"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </button>
                );
              })
            )}
          </div>

          {items.length > 0 && (
            <div className="border-t border-slate-800 px-4 py-2 flex items-center justify-between">
              <button
                data-testid="notif-clear-all"
                onClick={clearAll}
                className="text-[10px] text-rose-400 hover:text-rose-300 font-mono uppercase tracking-widest"
              >
                Tümünü Sil
              </button>
              <button
                onClick={() => { setOpen(false); navigate("/ayarlar?tab=bildirim"); }}
                className="text-[10px] text-slate-400 hover:text-slate-200 font-mono uppercase tracking-widest"
              >
                Ayarlar →
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
